import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ReactNode } from "react";
import { Experience, User } from "../../../models/user";
import { UserConversation } from "../../../models/userConversation";
dayjs.extend(utc);
dayjs.extend(timezone);

export const mergeDateTime = (
  inputDate: Dayjs | null,
  inputTime: Dayjs | null
) => {
  if (!inputDate) return null;
  if (inputTime)
    return dayjs(inputDate)
      .set("hour", dayjs(inputTime).hour())
      .set("minute", dayjs(inputTime).minute())
      .set("second", dayjs(inputTime).second())
      .set("millisecond", dayjs(inputTime).millisecond());
  return inputDate;
};

export const calculateWorkingDays = (
  startDate: Dayjs | null,
  endDate: Dayjs | null
) => {
  if (!startDate || !endDate) return 0;

  let workingDays = 0;
  let currentDate = dayjs(startDate).startOf("day");

  while (
    currentDate.isBefore(dayjs(endDate)) ||
    currentDate.isSame(dayjs(endDate))
  ) {
    if (currentDate.day() !== 0 && currentDate.day() !== 6) {
      workingDays++;
    }
    currentDate = currentDate.add(1, "day");
  }

  return workingDays;
};

export const isBeforeToday = (date: Dayjs) => {
  return dayjs(date).isBefore(dayjs().startOf("day"));
};

export const commify = (n: number) => {
  var parts = toFixed2(n).toString().split(".");
  const numberPart = parts[0];
  const decimalPart = parts[1];
  const thousands = /\B(?=(\d{3})+(?!\d))/g;
  return (
    numberPart.replace(thousands, " ") + (decimalPart ? "." + decimalPart : "")
  );
};

export const getCurrentDate = () => {
  let newDate = new Date();
  let date = newDate.getDate();
  let month = newDate.getMonth() + 1;
  let year = newDate.getFullYear();

  return `${year}-${month < 10 ? `0${month}` : `${month}`}-${date}`;
};

export const getDateAYearAgo = () => {
  let newDate = new Date();
  let date = newDate.getDate();
  let month = newDate.getMonth() + 1;
  let year = newDate.getFullYear() - 1;

  return `${year}-${month < 10 ? `0${month}` : `${month}`}-${date}`;
};

export const getStringFromDate = (date: Date) => {
  if (date === null || date === undefined) return null;
  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  return `${day}-${month < 10 ? `0${month}` : `${month}`}-${year}`;
};

export const getISODateFromString = (dateString: string) => {
  let date = new Date(dateString);
  return date.toLocaleDateString();
};

export const getTimeFromDate = (dateString: string) => {
  let date = new Date(dateString);

  let minutes = date.getHours();
  let seconds = date.getSeconds();

  return `${minutes < 10 ? `0${minutes}` : `${minutes}`}:${
    seconds < 10 ? `0${seconds}` : `${seconds}`
  }`;
};

export const parseToDate = (dateStr) => {
  const parts = dateStr.split("/");
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);

  return new Date(year, month, day);
};

export const calculateAge = (dob1) => {
  var today = new Date();
  var birthDate = new Date(dob1); // create a date object directly from `dob1` argument
  var age_now = today.getFullYear() - birthDate.getFullYear();
  var m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age_now--;
  }
  return age_now;
};

export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

export const stringAvatar = (name: string) => {
  const splitName = name.split(" ");
  const filteredName = splitName.filter(
    (word) => !["Mr.", "Mme."].includes(word)
  );

  let initial: ReactNode | undefined = undefined;
  if (filteredName.length > 1) {
    initial = `${filteredName[0][0]}${filteredName[1][0]}`;
  } else if (filteredName.length === 1) {
    initial = filteredName[0][0];
  }

  return {
    sx: {
      bgcolor: stringToColor(name),
      padding: "4px",
      fontSize: "14px",
      color: "white",
    },
    children: initial,
  };
};

export const isDateValid = (dayjsObject: Dayjs | null) => {
  if (!dayjsObject) return false;
  return dayjsObject instanceof dayjs && dayjsObject.isValid();
};

export const toFixed2 = (num: number | null | undefined) => {
  if (
    num === null ||
    num === undefined ||
    typeof num !== "number" ||
    isNaN(num)
  ) {
    return "";
  }
  if (Math.abs(num) < 0.01) {
    return "0.00";
  }
  return (+(Math.round(+(num + 0.0001 + "e" + 2)) + "e" + -2)).toFixed(2);
};

export const toFixed2Number = (num: number | null | undefined) => {
  if (
    num === null ||
    num === undefined ||
    typeof num !== "number" ||
    isNaN(num)
  ) {
    return 0;
  }
  if (Math.abs(num) < 0.01) {
    return 0;
  }
  return Number(
    (+(Math.round(+(num + 0.0001 + "e" + 2)) + "e" + -2)).toFixed(2)
  );
};

export const trimLongString = (str: string | null | undefined) => {
  if (str) {
    return str.length > 50 ? str.substring(0, 48) + "..." : str;
  }
  return "";
};

export const getRelativeTime = (
  date: Dayjs | null | undefined,
  t: any
): string => {
  const now = dayjs();
  const diff = now.diff(date, "second");

  if (diff < 60) {
    // quelques secondes
    return t("RELATIVE_SECONDS");
  }

  const minutes = Math.floor(diff / 60);
  if (diff < 60 * 60) {
    // minutes
    return t("RELATIVE_MINUTES", { count: minutes });
  }

  const hours = Math.floor(diff / 3600);
  if (diff < 3600 * 24) {
    return t("RELATIVE_HOURS", { count: hours });
  }

  const days = Math.floor(diff / (3600 * 24));
  if (diff < 3600 * 24 * 30) {
    return t("RELATIVE_DAYS", { count: days });
  }

  const months = Math.floor(diff / (3600 * 24 * 30));
  if (diff < 3600 * 24 * 30 * 12) {
    return t("RELATIVE_MONTHS", { count: months });
  }

  const years = Math.floor(diff / (3600 * 24 * 30 * 12));
  return t("RELATIVE_YEARS", { count: years });
};

export const getPresenceMessage = (user: User | null, t: any) => {
  if (!user) return "";

  return user.connected
    ? t("PRESENCE_ONLINE")
    : user.lastSeen
    ? t("PRESENCE_ONLINE") + " " + getRelativeTime(user.lastSeen, t)
    : t("PRESENCE_OFFLINE");
};

export const dateDifference = (
  startDate: Dayjs | null,
  endDate: Dayjs | null
): string => {
  if (!startDate || !endDate) return "";

  const adjustedStartDate = dayjs(startDate).startOf("second");
  const adjustedEndDate = dayjs(endDate).startOf("second");

  const duration = adjustedEndDate.diff(adjustedStartDate, "second"); // La différence en secondes

  const years = Math.floor(duration / (3600 * 24 * 365));
  const months = Math.floor((duration % (3600 * 24 * 365)) / (3600 * 24 * 30));
  const days = Math.floor(
    ((duration % (3600 * 24 * 365)) % (3600 * 24 * 30)) / (3600 * 24)
  );
  const hours = Math.floor(
    ((duration % (3600 * 24 * 365)) % (3600 * 24)) / 3600
  );
  const minutes = Math.floor(((duration % (3600 * 24 * 365)) % 3600) / 60);
  const seconds = Math.floor((duration % (3600 * 24 * 365)) % 60);

  const formatValue = (value: number, unit: string): string => {
    return `${value} ${unit}${value !== 1 ? "s" : ""}`;
  };

  const formattedYears = years > 0 ? formatValue(years, "année") : "";
  const formattedMonths = months > 0 ? formatValue(months, "mois") : "";
  const formattedDays = days > 0 ? formatValue(days, "jour") : "";
  const formattedHours = hours > 0 ? formatValue(hours, "heure") : "";
  const formattedMinutes = minutes > 0 ? formatValue(minutes, "minute") : "";
  const formattedSeconds = seconds > 0 ? formatValue(seconds, "seconde") : "";

  const formattedParts = [
    formattedYears,
    formattedMonths,
    formattedDays,
    formattedHours,
    formattedMinutes,
    formattedSeconds,
  ].filter(Boolean);

  return formattedParts.join(" ");
};

export const getUserFullName = (user: User | undefined | null) => {
  if (user) {
    return user.lastName + " " + user.firstName;
  }
  return "";
};

export const getUserInitials = (user: User | undefined | null) => {
  if (!user) return "";

  const firstInitial = user.firstName?.charAt(0) ?? "";
  const lastInitial = user.lastName?.charAt(0) ?? "";

  return (lastInitial + firstInitial).toUpperCase();
};

export const dayjsToStringWithSettings = (
  date: Dayjs | undefined | null,
  timezone: string = "Africa/Casablanca",
  dateFormat: string = "DD/MM/YYYY"
) => {
  if (!date) return "";
  return dayjs(date).tz(timezone).format(dateFormat);
};

export const dayjsToStringWithTimeAndSettings = (
  date: Dayjs | null | undefined,
  timezone: string = "Africa/Casablanca",
  dateFormat: string = "DD/MM/YYYY"
): string => {
  if (!date) return "";
  // Ajouter l'heure au format de date
  const formatWithTime = `${dateFormat} HH:mm`;
  return dayjs(date).tz(timezone).format(formatWithTime);
};

export function calculateTotalExperienceYears(
  experiences: Experience[]
): number {
  const now = dayjs();

  let totalMonths = 0;

  experiences.forEach((exp) => {
    if (!exp.startDate) return;

    const end = exp.current || !exp.endDate ? now : dayjs(exp.endDate);
    const months = end.diff(exp.startDate, "month");
    totalMonths += months;
  });

  return Math.floor(totalMonths / 12);
}

export const getUserDescription = (user: User | undefined | null) => {
  if (user) {
    return user.lastName + " " + user.firstName;
  }
  return "";
};

export const imageIsDefault = (imageUrl: string | undefined | null) => {
  if (imageUrl) {
    return imageUrl ===
      "https://" +
        process.env.REACT_APP_BUCKET_NAME! +
        ".s3.eu-west-3.amazonaws.com/DefaultImages/profil.png"
      ? true
      : false;
  }
  return true;
};

export const getChatOtherUserFullName = (
  conversation: UserConversation,
  userId: string | undefined | null
): string => {
  const otherUser =
    conversation.userA.id === userId ? conversation.userB : conversation.userA;
  return getUserFullName(otherUser);
};

export const getChatOtherUser = (
  conversation: UserConversation,
  userId: string | undefined | null
) => {
  return conversation.userA.id === userId
    ? conversation.userB
    : conversation.userA;
};

// Export du hook de formatage de dates
export { useDateFormatting } from "./useDateFormatting";
